<template>
  <div class="support-page">
    <div class="support-card" v-if="loading">
      <p class="muted">Loading campaign…</p>
    </div>

    <div class="support-card" v-else-if="!campaign">
      <h1>Campaign not found</h1>
      <p class="muted">Check the link with the club that sent it to you.</p>
    </div>

    <div class="support-card" v-else>
      <header class="support-header">
        <TenantLogo id="support-logo" logo-class="" />
        <p class="club-name">{{ campaign.club_name }}</p>
        <h1>{{ campaign.title }}</h1>
      </header>

      <div class="thanks-banner" v-if="justDonated">
        🎉 Thank you — your donation has been received!
      </div>
      <div class="cancel-banner" v-if="cancelled">
        No problem — you can donate any time while the campaign is open.
      </div>

      <!-- Progress ------------------------------------------------------- -->
      <div class="progress-block">
        <p class="raised">
          <strong>{{ pounds(campaign.raised_pence) }}</strong>
          <span v-if="campaign.target_pence"> of {{ pounds(campaign.target_pence) }}</span>
          raised
          <span class="muted" v-if="campaign.donation_count > 0">
            · {{ campaign.donation_count }} supporter{{ campaign.donation_count === 1 ? "" : "s" }}
          </span>
        </p>
        <div class="progress-track" v-if="campaign.target_pence">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <p class="muted deadline" v-if="campaign.deadline">
          Campaign ends {{ formatDate(campaign.deadline) }}
        </p>
      </div>

      <p class="description" v-if="campaign.description">{{ campaign.description }}</p>

      <!-- Donate --------------------------------------------------------- -->
      <div class="donate-block" v-if="campaign.status === 'active'">
        <h2>Make a donation</h2>
        <div class="amount-buttons">
          <button
            v-for="amount in suggestedAmounts"
            :key="amount"
            class="amount-btn"
            :class="{ selected: selectedAmount === amount && !customAmount }"
            @click="selectedAmount = amount; customAmount = ''"
          >
            {{ pounds(amount) }}
          </button>
          <div class="custom-amount">
            <span>{{ currencySymbol }}</span>
            <input
              type="number"
              min="1"
              step="0.01"
              placeholder="Other"
              v-model="customAmount"
            />
          </div>
        </div>
        <label class="anon-check">
          <input type="checkbox" v-model="anonymous" />
          Don't show my name on the supporters list
        </label>
        <button class="donate-btn" :disabled="donating || donationPence < 100" @click="donate">
          {{ donating ? "Opening secure checkout…" : `Donate ${donationPence >= 100 ? pounds(donationPence) : ""}` }}
        </button>
        <p class="muted small">Payments are processed securely by Stripe on behalf of the club.</p>
        <p class="error-text" v-if="error">{{ error }}</p>
      </div>
      <div class="donate-block closed" v-else>
        <p>This campaign has closed — thank you to everyone who supported it.</p>
      </div>

      <!-- Supporters ------------------------------------------------------ -->
      <div class="supporters" v-if="campaign.supporters && campaign.supporters.length > 0">
        <h3>Recent supporters</h3>
        <ul>
          <li v-for="(supporter, index) in campaign.supporters" :key="index">
            <span class="supporter-name">{{ supporter.name }}</span>
            <span class="supporter-amount">{{ pounds(supporter.amount_pence) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { getPaymentsCurrency, getCurrencySymbol } from "@/config/tenant";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import TenantLogo from "@/components/TenantLogo/index.vue";

export default {
  name: "SupportCampaign",
  components: { TenantLogo },
  data() {
    return {
      loading: true,
      campaign: null,
      selectedAmount: null,
      customAmount: "",
      anonymous: false,
      donating: false,
      error: "",
      justDonated: false,
      cancelled: false,
    };
  },
  computed: {
    currencySymbol() {
      return getCurrencySymbol();
    },
    clubId() {
      return this.$route.params.clubId;
    },
    campaignId() {
      return this.$route.params.campaignId;
    },
    suggestedAmounts() {
      const raw = this.campaign?.suggested_amounts || "";
      const values = raw
        .split(",")
        .map((v) => parseInt(v, 10))
        .filter((v) => v >= 100);
      return values.length > 0 ? values : [500, 1000, 2500];
    },
    donationPence() {
      if (this.customAmount) {
        return Math.round(parseFloat(this.customAmount) * 100) || 0;
      }
      return this.selectedAmount || 0;
    },
    progressPercent() {
      if (!this.campaign?.target_pence) return 0;
      return Math.min(
        100,
        Math.round((this.campaign.raised_pence / this.campaign.target_pence) * 100),
      );
    },
  },
  async created() {
    // Returning from Stripe: confirm before loading so the total includes it
    if (this.$route.query.donation === "success" && this.$route.query.session_id) {
      try {
        await StripeConnect.confirmDonation(this.clubId, this.$route.query.session_id);
        this.justDonated = true;
      } catch (error) {
        // The webhook will record it regardless
      }
    }
    this.cancelled = this.$route.query.donation === "cancelled";
    this.loadCampaign();
  },
  methods: {
    pounds(amountPence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: getPaymentsCurrency(),
        minimumFractionDigits: amountPence % 100 === 0 ? 0 : 2,
      }).format((amountPence || 0) / 100);
    },
    formatDate(value) {
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date)) return value;
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
    async loadCampaign() {
      this.loading = true;
      try {
        const response = await StripeConnect.getPublicCampaign(
          this.clubId,
          this.campaignId,
        );
        this.campaign = response.data.data;
        const [first] = this.suggestedAmounts;
        this.selectedAmount = first;
      } catch (error) {
        this.campaign = null;
      } finally {
        this.loading = false;
      }
    },
    async donate() {
      this.donating = true;
      this.error = "";
      try {
        const response = await StripeConnect.createDonationCheckout({
          clubId: this.clubId,
          campaignId: this.campaignId,
          amount_pence: this.donationPence,
          anonymous: this.anonymous,
        });
        window.location.href = response.data.data.url;
      } catch (error) {
        this.error =
          error?.response?.data?.error || "Could not start the donation.";
        this.donating = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.support-page {
  min-height: 100vh;
  background: #f5f6f8;
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;

  .support-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 2rem;
    max-width: 560px;
    width: 100%;
    height: fit-content;
  }

  .support-header {
    text-align: center;
    margin-bottom: 1.25rem;

    .club-name {
      color: #777;
      margin: 0.5rem 0 0.25rem;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    h1 {
      font-size: 1.5rem;
      margin: 0;
    }
  }

  .thanks-banner,
  .cancel-banner {
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .thanks-banner {
    background: #e6f6ec;
    color: #1e7b3c;
  }

  .cancel-banner {
    background: #f2f4f7;
    color: #555;
  }

  .progress-block {
    margin-bottom: 1.25rem;

    .raised {
      font-size: 1.1rem;
      margin-bottom: 0.4rem;

      strong {
        font-size: 1.4rem;
      }
    }

    .progress-track {
      height: 10px;
      border-radius: 999px;
      background: #edf0f3;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      border-radius: 999px;
      background: var(--brand-color, #1e87f0);
      transition: width 0.4s ease;
    }

    .deadline {
      margin-top: 0.4rem;
    }
  }

  .description {
    white-space: pre-line;
    color: #444;
    margin-bottom: 1.5rem;
  }

  .donate-block {
    border-top: 1px solid #eee;
    padding-top: 1.25rem;

    h2 {
      font-size: 1.1rem;
      margin: 0 0 0.75rem;
    }

    &.closed {
      text-align: center;
      color: #555;
    }
  }

  .amount-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    .amount-btn {
      border: 1px solid #ccc;
      background: #fff;
      color: #333;
      border-radius: 8px;
      padding: 0.5rem 1rem;
      font-size: 1rem;
      cursor: pointer;

      &.selected {
        border-color: var(--brand-color, #1e87f0);
        box-shadow: 0 0 0 1px var(--brand-color, #1e87f0) inset;
        font-weight: 600;
      }
    }

    .custom-amount {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      border: 1px solid #ccc;
      border-radius: 8px;
      padding: 0 0.5rem;
      color: #555;

      input {
        border: none;
        outline: none;
        width: 5rem;
        padding: 0.5rem 0.25rem;
        font-size: 1rem;
      }
    }
  }

  .anon-check {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
    color: #555;
    margin-bottom: 0.9rem;

    input {
      margin: 0;
    }
  }

  .donate-btn {
    width: 100%;
    background: var(--brand-color, #1e87f0);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.8rem;
    font-size: 1.05rem;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .small {
    font-size: 0.75rem;
    text-align: center;
    margin-top: 0.5rem;
  }

  .muted {
    color: #777;
  }

  .error-text {
    color: #c0392b;
    margin-top: 0.5rem;
  }

  .supporters {
    border-top: 1px solid #eee;
    margin-top: 1.5rem;
    padding-top: 1rem;

    h3 {
      font-size: 0.95rem;
      margin: 0 0 0.5rem;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      display: flex;
      justify-content: space-between;
      padding: 0.35rem 0;
      border-bottom: 1px solid #f4f4f4;
      font-size: 0.9rem;

      .supporter-name {
        color: #444;
      }

      .supporter-amount {
        color: #1e7b3c;
        font-weight: 600;
      }
    }
  }
}
</style>
